import React, { useEffect, useMemo, useState } from "react";
import { Plus, Minus } from "react-feather";
import { toast } from "react-toastify";
import {
  submitTeacherLedgerAdjustment,
  type TeacherLedgerAdjustmentType,
} from "../../../../Api/apiUtils";
import { MixpanelEvents, trackEvent } from "../../../../utils/mixpanelTracking";
import { Button, Dialog, Field, SegmentedControl, Select, TextArea, TextInput } from "../../../../ui";

export type AdjustmentModalTarget =
  | { mode: "create" }
  | {
      mode: "update_line";
      ledgerLineId: number;
      lineSummary?: string;
      defaultAmount?: number;
    };

const ADJUSTMENT_REASON_CATEGORIES = [
  { value: "", label: "— Select reason category —" },
  {
    value: "CLASS_CONDUCTED_NOT_LOGGED",
    label: "Class conducted but not logged in initial run",
  },
  {
    value: "TEACHER_NOSHOW_PENALTY_ERROR",
    label: "Teacher no-show penalty applied in error — verified present",
  },
  {
    value: "INCORRECT_STUDENT_LEVEL",
    label: "Incorrect student level used for rate calculation",
  },
  {
    value: "INCORRECT_STUDENT_LOCATION",
    label: "Incorrect student location used (India vs International)",
  },
  {
    value: "LEAVE_NOTICE_VERIFIED",
    label: "Leave notice timestamp verified — late-notice flag removed",
  },
  {
    value: "EMERGENCY_BLOCK_REVERSED",
    label: "Emergency block penalty reversed — within free block allowance",
  },
  {
    value: "RENEWAL_BONUS_RATE_CORRECTED",
    label: "Renewal bonus rate corrected (success % recalculated)",
  },
  { value: "DUPLICATE_ENTRY_REMOVED", label: "Duplicate entry removed" },
  { value: "VOLUME_BONUS_TIER_CORRECTION", label: "Volume bonus tier correction" },
  { value: "DISPUTE_RESOLVED", label: "Dispute resolved — see dispute ref" },
  { value: "OTHER", label: "Other (describe below)" },
];

const ADJUSTMENT_TYPE_OPTIONS: {
  value: TeacherLedgerAdjustmentType;
  label: string;

  icon: React.ReactNode;
  tone: "credit" | "deduction";
}[] = [
  {
    value: "CREDIT",
    label: "Add credit",

    icon: <Plus size={14} aria-hidden />,
    tone: "credit",
  },
  {
    value: "DEDUCTION",
    label: "Add deduction",
 
    icon: <Minus size={14} aria-hidden />,
    tone: "deduction",
  },
];

const resolveApiAmount = (raw: number, type: TeacherLedgerAdjustmentType): number => {
  if (!Number.isFinite(raw) || raw === 0) return 0;
  if (raw < 0) return raw;
  return type === "DEDUCTION" ? -Math.abs(raw) : Math.abs(raw);
};

const sanitizeAmountInput = (value: string): string => {
  if (!value) return "";
  const cleaned = value.replace(/[^\d.]/g, "");
  const dotIndex = cleaned.indexOf(".");
  if (dotIndex === -1) return cleaned;
  return `${cleaned.slice(0, dotIndex + 1)}${cleaned.slice(dotIndex + 1).replace(/\./g, "")}`;
};

const parseAmountValue = (value: string): number => {
  const trimmed = value.trim();
  if (!trimmed || trimmed === ".") return NaN;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : NaN;
};

type Props = {
  isOpen: boolean;
  teacherId: string | number;
  teacherName?: string;
  target: AdjustmentModalTarget | null;
  onClose: () => void;
  onSuccess: () => void;
};

const TeacherLedgerAdjustmentModal: React.FC<Props> = ({
  isOpen,
  teacherId,
  teacherName,
  target,
  onClose,
  onSuccess,
}) => {
  const isUpdateLine = target?.mode === "update_line";

  const [adjustmentType, setAdjustmentType] = useState<TeacherLedgerAdjustmentType>("CREDIT");
  const [amount, setAmount] = useState("");
  const [reasonCategory, setReasonCategory] = useState("");
  const [teacherNote, setTeacherNote] = useState("");
  const [classTiming, setClassTiming] = useState<30 | 60 | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setAdjustmentType("CREDIT");
    setAmount("");
    setReasonCategory("");
    setTeacherNote("");
    setClassTiming(null);
  };

  useEffect(() => {
    if (!isOpen) return;
    resetForm();
    if (target?.mode === "update_line" && target.defaultAmount != null) {
      const abs = Math.abs(target.defaultAmount);
      setAmount(String(abs));
      setAdjustmentType(target.defaultAmount < 0 ? "DEDUCTION" : "CREDIT");
    }
  }, [isOpen, target]);

  const handleClose = () => {
    if (submitting) return;
    resetForm();
    onClose();
  };

  const parsedAmount = parseAmountValue(amount);
  const apiAmount = useMemo(
    () => resolveApiAmount(parsedAmount, adjustmentType),
    [parsedAmount, adjustmentType]
  );

  const reasonLabel = useMemo(
    () => ADJUSTMENT_REASON_CATEGORIES.find((c) => c.value === reasonCategory)?.label ?? "",
    [reasonCategory]
  );

  const handleSubmit = async () => {
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      toast.error("Please enter a valid amount greater than zero.");
      return;
    }
    if (!reasonCategory) {
      toast.error("Please select a reason category.");
      return;
    }
    const note = teacherNote.trim();
    if (!note) {
      toast.error("Please enter a description for the teacher.");
      return;
    }

    const tid = Number(teacherId);
    if (!Number.isFinite(tid)) {
      toast.error("Missing teacher id.");
      return;
    }

    setSubmitting(true);
    try {
      if (isUpdateLine && target?.mode === "update_line") {
        await submitTeacherLedgerAdjustment({
          action: "UPDATE_LINE",
          ledgerLineId: target.ledgerLineId,
          teacherId: tid,
          update: {
            amount: apiAmount,
            notes: note,
          },
          reason: reasonLabel,
          description: note,
          ...(classTiming != null ? { classTiming } : {}),
        });
      } else {
        await submitTeacherLedgerAdjustment({
          action: "CREATE_LINE",
          line: {
            teacherId: tid,
            amount: apiAmount,
          },
          reason: reasonLabel,
          description: note,
        });
      }
      trackEvent(MixpanelEvents.TEACHER_LEDGER_ADJUSTMENT_SUBMITTED_SUCCESS, {
        teacherId: tid,
        teacherName: teacherName ?? null,
        adjustmentMode: isUpdateLine ? "update_line" : "create",
        action: isUpdateLine ? "UPDATE_LINE" : "CREATE_LINE",
        amount: apiAmount,
        reason: reasonLabel,
        classTiming: isUpdateLine ? classTiming : null,
        ledgerLineId: isUpdateLine && target?.mode === "update_line" ? target.ledgerLineId : null,
      });
      toast.success("Adjustment saved.");
      resetForm();
      onSuccess();
      onClose();
    } catch (error: unknown) {
      const ax = error as { response?: { data?: { message?: string; error?: string } } };
      const errorMessage =
        ax?.response?.data?.message ||
        ax?.response?.data?.error ||
        "Failed to save adjustment.";
      trackEvent(MixpanelEvents.TEACHER_LEDGER_ADJUSTMENT_SUBMITTED_FAILURE, {
        teacherId: tid,
        teacherName: teacherName ?? null,
        adjustmentMode: isUpdateLine ? "update_line" : "create",
        action: isUpdateLine ? "UPDATE_LINE" : "CREATE_LINE",
        amount: apiAmount,
        reason: reasonLabel,
        classTiming: isUpdateLine ? classTiming : null,
        ledgerLineId: isUpdateLine && target?.mode === "update_line" ? target.ledgerLineId : null,
        errorMessage,
      });
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      title={isUpdateLine ? "Review adjustment" : "Add an adjustment"}
      actions={
        <>
          <Button variant="secondary" disabled={submitting} onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={submitting} onClick={() => void handleSubmit()}>
            {submitting ? "Saving…" : "Add adjustment"}
          </Button>
        </>
      }
    >
      <div className="tl-adjustment">
        {teacherName ? <p className="tl-adjustment__teacher ds-strong">{teacherName}</p> : null}

        {isUpdateLine && target?.lineSummary ? (
          <div className="tl-adjustment__line-ref">{target.lineSummary}</div>
        ) : null}

        <Field label="Type of adjustment">
          {() => (
            <SegmentedControl<TeacherLedgerAdjustmentType>
              label="Type of adjustment"
              value={adjustmentType}
              onChange={setAdjustmentType}
              disabled={submitting}
              options={ADJUSTMENT_TYPE_OPTIONS.map((opt) => ({
                value: opt.value,
                label: (
                  <span className="tl-adjustment__type-option">
                    {opt.icon}
                    {opt.label}
                  </span>
                ),
              }))}
            />
          )}
        </Field>

        <div className="tl-adjustment__row">
          <Field label="Amount (₹)" required>
            {(ctl) => (
              <TextInput
                {...ctl}
                type="text"
                numeric
                placeholder="e.g. 360"
                value={amount}
                onChange={(e) => setAmount(sanitizeAmountInput(e.target.value))}
                onKeyDown={(e) => {
                  if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
                }}
                disabled={submitting}
              />
            )}
          </Field>

          {isUpdateLine ? (
            <Field label="Class timing" hint="Optional. Click again to clear.">
              {() => (
                <SegmentedControl
                  label="Class timing"
                  value={classTiming != null ? String(classTiming) : ""}
                  onChange={(value) => {
                    const mins = Number(value) as 30 | 60;
                    setClassTiming((prev) => (prev === mins ? null : mins));
                  }}
                  disabled={submitting}
                  options={([30, 60] as const).map((mins) => ({ value: String(mins), label: `${mins} min` }))}
                />
              )}
            </Field>
          ) : null}
        </div>

        <Field label="Category" required>
          {(ctl) => (
            <Select
              {...ctl}
              options={ADJUSTMENT_REASON_CATEGORIES}
              value={reasonCategory}
              onChange={(e) => setReasonCategory(e.target.value)}
              disabled={submitting}
            />
          )}
        </Field>

        <Field label="Description" required hint="Shown to the teacher.">
          {(ctl) => (
            <TextArea
              {...ctl}
              rows={2}
              placeholder="Describe this adjustment for the teacher..."
              value={teacherNote}
              onChange={(e) => setTeacherNote(e.target.value)}
              disabled={submitting}
              required
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
};

export default TeacherLedgerAdjustmentModal;
